import { Link } from "react-router-dom";
import { ArrowRight, PhoneCall, Compass, FileText, PenTool, Repeat } from "lucide-react";

export default function OurStrategy() {
  return (
    <div>
      {/* Header */}
      <section className="border-b hairline">
        <div className="max-w-7xl mx-auto px-5 md:px-8 pt-16 md:pt-20 pb-14">
          <div className="eyebrow mb-6">Our Strategy</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-ink max-w-4xl">
            Why we don't sell "products" —<br />we build <span className="italic text-brass-deep">one plan</span>.
          </h1>
          <p className="mt-8 text-[16px] md:text-[17px] text-ink-soft leading-relaxed max-w-3xl">
            Ask a typical advisor for help and you'll usually get pointed toward a product: a mutual
            fund, a policy, a plan. We start differently — with your life, not a product catalogue.
          </p>
        </div>
      </section>

      {/* Where it all starts */}
      <section className="border-b hairline bg-paper-raised">
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-20 md:py-24">
          <div className="grid md:grid-cols-12 gap-10">
            <div className="md:col-span-5">
              <div className="eyebrow mb-4">Where it all starts</div>
              <div className="card-paper p-8 ledger-rule">
                <div className="num text-3xl md:text-4xl text-ink tracking-tight">Income − Expenses = Savings.</div>
                <p className="mt-4 text-[14px] text-ink-soft leading-relaxed">
                  What's left over once your expenses are met is what fuels everything else — the
                  savings you invest to generate the wealth needed to meet your goals and create
                  real, lasting value for you and your family.
                </p>
              </div>
            </div>

            <div className="md:col-span-7">
              <p className="text-[16px] text-ink leading-relaxed max-w-2xl">
                Every financial strategy we build rests on two things happening together:
              </p>

              <div className="mt-8 grid md:grid-cols-1 gap-5">
                <TwinCard
                  label="Building"
                  title="Goal-Based Investing"
                  body="Every rupee you invest should be working toward something specific. We understand where you are in your investment journey and help you plan and invest in a goal-based mutual fund portfolio — built for one purpose: wealth creation and growth, aimed at beating inflation over time, not just keeping up with it."
                />
                <TwinCard
                  label="Protecting"
                  title="The Right Cover, Sized Correctly"
                  body="Wealth that's being built also needs to be defended. Alongside your investment plan, we assess exactly what needs protecting — your income, your family, the loan you've taken on, the goal you're funding — and structure the right insurance cover, whose one job is protection, so that a single unexpected event can't undo years of disciplined saving."
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Two purpose-built tools */}
      <section className="border-b hairline">
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-20 md:py-24">
          <div className="grid md:grid-cols-12 gap-10">
            <div className="md:col-span-4">
              <div className="eyebrow">How we think about this</div>
              <h2 className="font-display text-3xl md:text-4xl mt-4 text-ink leading-tight">
                Two purpose-built tools —<br />each doing what it does best.
              </h2>
            </div>
            <div className="md:col-span-8 space-y-5">
              <p className="text-[16px] text-ink leading-relaxed">
                Investments and insurance are two different tools, and we generally prefer to use each
                one for what it's designed to do.
              </p>

              <div className="grid md:grid-cols-2 gap-5">
                <PillCard
                  label="Investments"
                  body="A goal-based mutual fund portfolio — best suited to create and grow your wealth over time."
                />
                <PillCard
                  label="Insurance"
                  body="Best suited to protect you and your family from events you can't predict."
                />
              </div>

              <p className="text-[15px] text-ink-soft leading-relaxed">
                Some products blend the two — combining a savings or investment component with an
                insurance cover. These can suit certain situations, and where they genuinely fit a
                client's goals we'll discuss them openly. In most cases, though, we find that keeping
                the two purposes distinct lets each one work harder: the investment side has room to
                pursue inflation-beating growth, and the protection side stays large and affordable
                enough to actually shield the plan. It's a preference, not a rule — and the right
                mix depends on your specific circumstances.
              </p>

              <p className="text-[15px] text-ink font-medium">
                So while your strategy is one integrated plan, the instruments inside it are each
                chosen for the job they do best.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why this has to be one decision */}
      <section className="border-b hairline bg-paper-raised">
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-20 md:py-24">
          <div className="eyebrow mb-4">Why this has to be one decision, not two</div>
          <h2 className="font-display text-3xl md:text-4xl text-ink leading-tight max-w-3xl">
            "Holistic" means <span className="italic text-brass-deep">one strategy</span> —<br />
            not two departments working separately.
          </h2>

          <div className="mt-10 grid md:grid-cols-3 gap-5">
            <ScenarioCard
              title="Buying a home"
              body="isn't just a savings plan — it's also making sure the loan doesn't become a burden on your family if something happens to the earning member paying it off."
            />
            <ScenarioCard
              title="Funding a child's education"
              body="isn't just a SIP — it's making sure that goal completes on schedule even if you're not there to keep contributing to it."
            />
            <ScenarioCard
              title="Planning retirement"
              body="isn't just about the corpus — it's about making sure a health emergency in your 60s doesn't consume the very corpus you spent decades building."
            />
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="border-b hairline">
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-20 md:py-24">
          <div className="eyebrow mb-4">What actually happens when you reach out</div>
          <h2 className="font-display text-3xl md:text-4xl text-ink leading-tight max-w-3xl">
            No black box.<br />Here's the sequence, step by step.
          </h2>

          <ol className="mt-12 space-y-3">
            <ProcessStep n="01" icon={<PhoneCall size={18} />} title="Discovery Call — No Obligation" body="A short conversation to understand your goals, your current investments and cover, and where you feel uncertain. Nothing is sold on this call." />
            <ProcessStep n="02" icon={<Compass size={18} />} title="Goal Mapping & Risk Profiling" body="We map out your specific goals — home, education, marriage, retirement, travel — and understand your comfort with risk, so the plan fits your life, not a template." />
            <ProcessStep n="03" icon={<FileText size={18} />} title="A Written Plan You Can Review" body="You get a clear, written strategy covering both sides — the investment plan to build toward your goals, and the protection cover needed to safeguard them — before you commit to anything." />
            <ProcessStep n="04" icon={<PenTool size={18} />} title="Execution" body="Once you're comfortable, we help you set up the investments and insurance cover the plan calls for — cleanly, and only through instruments purpose-built for the job they're meant to do." />
            <ProcessStep n="05" icon={<Repeat size={18} />} title="Ongoing Review" body="Markets move, goals shift, families grow. We review your plan at least once a year, and any time a major life event happens — a new child, a job change, a loan, a health event." />
          </ol>

          <div className="mt-12">
            <a
              href="https://reliservepulse.com/book-consultation"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-brass"
              data-testid="strategy-cta"
            >
              Start Building Your Strategy <ArrowRight size={16} />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}

function TwinCard({ label, title, body }) {
  return (
    <div className="card-paper p-6 ledger-rule">
      <div className="eyebrow">{label}</div>
      <div className="font-display text-[22px] mt-2 text-ink">{title}</div>
      <p className="text-[14.5px] text-ink-soft leading-relaxed mt-3">{body}</p>
    </div>
  );
}

function PillCard({ label, body }) {
  return (
    <div className="p-5 card-paper">
      <div className="inline-block px-3 py-1 border border-brass text-brass-deep num text-[11px] tracking-wider mb-3">
        {label.toUpperCase()}
      </div>
      <p className="text-[14.5px] text-ink-soft leading-relaxed">{body}</p>
    </div>
  );
}

function ScenarioCard({ title, body }) {
  return (
    <div className="card-paper p-6">
      <div className="font-display text-[19px] text-ink">{title}</div>
      <p className="text-[14px] text-ink-soft leading-relaxed mt-2">{body}</p>
    </div>
  );
}

function ProcessStep({ n, icon, title, body }) {
  return (
    <li className="card-paper p-6 flex gap-6">
      <div className="shrink-0 flex flex-col items-center gap-3">
        <div className="num text-brass-deep text-[15px]">{n}</div>
        <div className="w-8 h-8 rounded-full bg-paper flex items-center justify-center border hairline text-brass">
          {icon}
        </div>
      </div>
      <div className="flex-1">
        <div className="font-display text-[20px] text-ink">{title}</div>
        <p className="text-[14.5px] text-ink-soft leading-relaxed mt-2">{body}</p>
      </div>
    </li>
  );
}
